import axios from "./axios";
import { fetchUserOwnersWithWorkspaces } from "./index";

type ConversationLike = {
  unreadCount?: number;
};

export function countUnreadChats(conversations: ConversationLike[]): number {
  return conversations.filter((conv) => Number(conv?.unreadCount || 0) > 0).length;
}

let inboxUnreadChatsCount = 0;
const listeners = new Set<(count: number) => void>();

export function getInboxUnreadChatsCount(): number {
  return inboxUnreadChatsCount;
}

export function setInboxUnreadChatsCount(count: number): void {
  const next = Math.max(0, Math.trunc(Number(count) || 0));
  if (next === inboxUnreadChatsCount) return;
  inboxUnreadChatsCount = next;
  listeners.forEach((listener) => listener(next));
}

export function subscribeInboxUnreadChatsCount(
  listener: (count: number) => void
): () => void {
  listeners.add(listener);
  listener(inboxUnreadChatsCount);
  return () => {
    listeners.delete(listener);
  };
}

type InboxUnreadScope = {
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
};

type ConnectedAccount = {
  _id: string;
  hasLinkedinToken?: boolean;
  isDisconnected?: boolean;
};

async function resolveFirstConnectedOwner(
  options: InboxUnreadScope
): Promise<ConnectedAccount | null> {
  const result = await fetchUserOwnersWithWorkspaces(
    1,
    100,
    "",
    options.userId,
    options.workspaceId,
    options.ownerId
  );
  const accounts = (Array.isArray(result?.accounts) ? result.accounts : []).filter(
    (account: ConnectedAccount) =>
      Boolean(account?._id) &&
      Boolean(account?.hasLinkedinToken) &&
      !Boolean(account?.isDisconnected)
  );
  return accounts[0] || null;
}

export async function fetchInboxUnreadChatsCount(
  options: InboxUnreadScope
): Promise<number> {
  try {
    const firstAccount = await resolveFirstConnectedOwner(options);
    if (!firstAccount?._id) return 0;

    const response = await axios.post("/linkedin/inboxConversations", {
      ownerId: firstAccount._id,
    });
    const code = String(response?.data?.code || "")
      .trim()
      .toUpperCase();
    if (code !== "OK") return 0;
    const conversations = Array.isArray(response?.data?.conversations)
      ? response.data.conversations
      : [];
    return countUnreadChats(conversations);
  } catch {
    return inboxUnreadChatsCount;
  }
}

export async function refreshInboxUnreadChatsCount(
  options: InboxUnreadScope
): Promise<number> {
  const count = await fetchInboxUnreadChatsCount(options);
  setInboxUnreadChatsCount(count);
  return count;
}
